// COP — encrypted mission-room chat panel (React shell only; never enters the render loop).
//
// The full §6/§7 chat surface over the C3 @tak-tdf/cap-chat capability's ChatApi
// (CONTRACT:C3-CAPABILITY-PLUGIN.1.0 / C4-DESIGN-TOKENS.1.0). It is PURE React over the
// narrow ChatApi (rooms / messages / send / subscribe / read-state / DM / the bridge toggle
// surface) — it holds NO envelope/crypto logic and never touches HostContext. Messages flow
// as canonical envelopes through the capability-host bus; this panel only renders the DECODED
// view and pushes sends back through `api.send` (sealed) or the armed plaintext-bridge path.
//
// SECURITY-FAITHFUL RENDER (the load-bearing contract of this wave):
//   - §4.4 GLOMAR: the room list is the api.rooms() fold — a DENIED room is ABSENT
//     (no row, no badge, no "denied" affordance). We render exactly what the fold returns;
//     unread badges fold off the SAME post-seam set, so a hidden room can never surface one.
//   - §7.9 REDACTED: a `redacted` ChatMessage shows the MARKED PLACEHOLDER ("🔒 PROTECTED
//     <CLASS> — body sealed") and NEVER the body. The body is only rendered (with @mention
//     highlighting) when the capability already decoded it (a granted KAS rewrap). Fail-closed.
//   - §5.3 PLAINTEXT BRIDGE: the bridge control is HIDDEN unless api.bridgeReachable(); a
//     per-message CONFIRM modal DISPLAYS THE ACTUAL LEGACY CALLSIGNS (api.legacyCallsigns())
//     before arming; while armed a full-width amber/black HAZARD BANNER + countdown rides the
//     header/composer; window blur disarms.
//
// UX (Bundle C polish, all on the UX side of the crypto boundary): an ALWAYS-VISIBLE room
// list (not a dropdown), numeric UNREAD badges (per-channel + a global total surfaced on the
// rail), a first-class "+ New DM" people picker (a DM is a room with one other person), a
// jump-to-latest affordance, @mention highlighting, and flexible expand/collapse of the
// channel list / roster / an expand-to-large chat mode.
//
// Themed via the design-tokens CSS variables on :root (CONTRACT:C4-DESIGN-TOKENS.1.0),
// styled in chat.css to match the instrument housing (.panel).

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { ChatApi, ChatMessage, RoomRow } from "../capabilities/chatCapability.ts";
import { ALL_CHAT_ROOMS, isDmRoom, dmPeer } from "../capabilities/chatCapability.ts";
import { bannerColor, bannerTextColor } from "./dod-marking.ts";
import {
  CLASSIFICATIONS,
  RELEASABILITIES,
  classLabel,
  withinCeiling,
  renderMessageBody,
  renderUnreadBadge,
} from "./chatVocab.ts";
import { pushToast } from "./Toasts.tsx";
import { MessageTrustCard } from "./MessageTrustCard.tsx";
import { verifyMessageSignature, type SignatureVerifyState } from "../../../../packages/cap-chat/src/messages.ts";
import "./chat.css";

/** The bridge auto-revert window (mirrors the adapter's default `bridgeTimeoutMs`, §5.3) —
 *  used ONLY to drive the cosmetic countdown. The AUTHORITATIVE revert is the adapter's
 *  one-shot timer; this is a display estimate that resets if we re-observe an armed state. */
const BRIDGE_WINDOW_MS = 20_000;

/** Short HH:MM clock for a message timestamp. */
function clock(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "--:--";
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
}

/** TAK's canonical conversation sections (§7.1). A room is bucketed by name/shape so the
 *  list groups it the way ATAK does. */
type Section = "broadcast" | "teams" | "groups" | "contacts";

const TEAM_COLORS = new Set([
  "White", "Yellow", "Orange", "Magenta", "Red", "Maroon", "Purple", "Dark Blue",
  "Blue", "Cyan", "Teal", "Green", "Dark Green", "Brown",
]);

/** Bucket a room row into its §7.1 section. Broadcast is the fixed room; a DM (the `dm:`
 *  convention) and a 1:1 contact conversation land in Contacts; a 14-color name is a team
 *  room; everything else is a user-made Group. Display-only grouping (never a gate). */
function sectionOf(row: RoomRow): Section {
  if (row.fixed || row.room === ALL_CHAT_ROOMS) return "broadcast";
  if (isDmRoom(row.room)) return "contacts";
  if (TEAM_COLORS.has(row.room)) return "teams";
  // A 1:1 contact conversation key is a callsign/uid with no spaces and no descriptor-set
  // display name distinct from the key. Heuristic + display-only (never a gate).
  if (row.displayName === row.room && !row.room.includes(" ")) return "contacts";
  return "groups";
}

const SECTION_LABEL: Record<Section, string> = {
  broadcast: "All Chat Rooms",
  teams: "Teams",
  groups: "Groups",
  contacts: "Direct & Contacts",
};

/** Resolve a DM peer id → its callsign when known (so the DM header/list shows "DOOGIE-42"
 *  not the raw email/id), falling back to the id. Callsign is client-asserted display only. */
function peerDisplay(peerId: string, callsignOf?: Map<string, string>): string {
  return callsignOf?.get(peerId) ?? peerId;
}

/** The display label for a room row — the DM peer for a DM (resolved per viewer, callsign when
 *  known), else the descriptor display name, else the room key. */
function roomLabel(row: RoomRow, self: string, callsignOf?: Map<string, string>): string {
  if (isDmRoom(row.room)) {
    const peer = dmPeer(row.room, self);
    return peer ? peerDisplay(peer, callsignOf) : row.room;
  }
  return row.displayName;
}

/** A per-message / per-room classification chip — colored by the standard US marking color. */
function ClassChip({ classification, title }: { classification: string; title?: string }): JSX.Element {
  return (
    <span
      className="chat__chip"
      style={{ background: bannerColor(classification), color: bannerTextColor(classification) }}
      title={title ?? classLabel(classification)}
    >
      {classLabel(classification)}
    </span>
  );
}

export function ChatPanel({ api }: { api: ChatApi }): JSX.Element {
  // One subscription drives the whole panel: every store/roster/bridge/read-state change
  // bumps `tick` and we re-pull the fresh folds (rooms/messages/bridge state are pure reads
  // off the post-seam store — never a side-channel for a hidden room).
  const [, setTick] = useState(0);
  useEffect(() => api.subscribe(() => setTick((n) => n + 1)), [api]);
  // SESSION-STATE POLL: the token can expire with NO store event, so a slow tick keeps the
  // honest session banner (below) appearing/clearing even in a silent room.
  useEffect(() => {
    const t = window.setInterval(() => setTick((n) => n + 1), 10_000);
    return () => window.clearInterval(t);
  }, []);

  const room = api.room;
  const rooms = api.rooms();
  const messages = api.messages();
  const author = api.author;
  const auth = api.authState();
  // id → display-callsign map (client-asserted display data from presence beacons).
  const callsignOf = new Map<string, string>();
  for (const c of api.contacts()) if (c.callsign) callsignOf.set(c.id, c.callsign);

  // --- bridge state (re-derived each render off the live ChatApi) ---
  const bridgeReachable = api.bridgeReachable();
  const bridgeArmed = api.isBridgeArmed();
  const legacy = useMemo(() => api.legacyCallsigns(), [api, room, bridgeReachable, bridgeArmed]);

  // The current ROOM CLASS (from its row) — the composer marking defaults to it, and it is
  // the CEILING for the pick (§7.9, L2 flip: type at-or-below the room class, never above).
  const currentRow = rooms.find((r) => r.room === room);
  const roomClass = currentRow?.classification ?? "UNCLASSIFIED";
  // The current room's RELEASABILITY (audience) — the composer RelPicker re-pins to it on
  // room switch (H1b) so the operator default is the room audience, not a fixed value. The
  // capability CLAMPS a wider pick to the room audience regardless, but defaulting correct
  // avoids a confusing silent clamp. `undefined` (no descriptor) → default to the public,
  // UNRESTRICTED audience `[]` (NOT a coalition tag like NATO/FVEY, which would NARROW the
  // audience and — for NATO — is not even a legal defense.local releasability, so it threw
  // ILLEGAL_RELEASABILITY on every seal). `[]` validates + seals as release-to-everyone.
  const roomRel = currentRow?.floorReleasability;

  return (
    <ChatPanelInner
      api={api}
      room={room}
      author={author}
      rooms={rooms}
      messages={messages}
      roomClass={roomClass}
      roomRel={roomRel}
      bridgeReachable={bridgeReachable}
      bridgeArmed={bridgeArmed}
      legacy={legacy}
      auth={auth}
      callsignOf={callsignOf}
    />
  );
}

/** The render body — split so local UI state (draft / open panes / marking selection /
 *  the confirm modal / expand+collapse) is owned here while the parent owns the api
 *  subscription + folds. */
function ChatPanelInner({
  api,
  room,
  author,
  rooms,
  messages,
  roomClass,
  roomRel,
  bridgeReachable,
  bridgeArmed,
  legacy,
  auth,
  callsignOf,
}: {
  api: ChatApi;
  room: string;
  author: string;
  rooms: RoomRow[];
  messages: ChatMessage[];
  roomClass: string;
  roomRel?: string[];
  bridgeReachable: boolean;
  bridgeArmed: boolean;
  legacy: string[];
  /** Live access-token state (the honest session banner). */
  auth: ReturnType<ChatApi["authState"]>;
  /** id → display-callsign map (client-asserted; callsign-first rendering). */
  callsignOf: Map<string, string>;
}): JSX.Element {
  const [draft, setDraft] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [dmOpen, setDmOpen] = useState(false);
  const [confirmBridge, setConfirmBridge] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  // --- expand / collapse aspects (item 7) ---
  const [expanded, setExpanded] = useState(false);
  const [roomsCollapsed, setRoomsCollapsed] = useState(false);
  const [rosterCollapsed, setRosterCollapsed] = useState(false);
  const listRef = useRef<HTMLDivElement | null>(null);

  // The composer marking — defaults to the ROOM CLASS (§7.9), re-pinned on room switch.
  // CEILING (L2 flip): the operator may LOWER the content pick below the room class but
  // never lift it above (the capability's assertRoomMarking is the authoritative gate —
  // ROOM_CEILING; we grey an invalid pick before the round-trip).
  const [marking, setMarking] = useState<string>(roomClass);
  const [rel, setRel] = useState<string[]>(roomRel ?? []);
  useEffect(() => {
    // On room switch, PIN the marking to the new room's class (never carry a stale pick —
    // possibly ABOVE the new room's ceiling — across rooms).
    setMarking(roomClass);
  }, [room, roomClass]);
  useEffect(() => {
    // H1b — on room switch, RE-PIN the RelPicker to the room's RELEASABILITY (not a fixed
    // value carried across rooms). The capability clamps a wider pick to the room audience
    // regardless, but defaulting to it avoids a silent-clamp surprise. No descriptor →
    // public/unrestricted `[]` (see roomRel note above).
    setRel(roomRel ?? []);
  }, [room, roomRel]);
  // CEILING clamp-DOWN (the inverse of the old lift-to-floor): if the current pick sits
  // ABOVE the room class (a stale pick, or the room class dropped/arrived via descriptor),
  // CLAMP DOWN to the room class — the composer can never sit above the ceiling, and it is
  // NEVER lifted up (a lower content pick is always legal).
  const effectiveMarking = withinCeiling(marking, roomClass) ? marking : roomClass;

  // --- the cosmetic bridge countdown (§5.3). The authoritative revert is the adapter's
  // one-shot timer; this just drives the visible counter + the revert toast. ---
  const [armedAt, setArmedAt] = useState<number | null>(null);
  const [remainingMs, setRemainingMs] = useState(BRIDGE_WINDOW_MS);
  const prevArmed = useRef(false);
  useEffect(() => {
    if (bridgeArmed && !prevArmed.current) {
      // Just armed — start the countdown.
      setArmedAt(Date.now());
      setRemainingMs(BRIDGE_WINDOW_MS);
    } else if (!bridgeArmed && prevArmed.current) {
      // Just reverted (timer / send / blur / state-change) — encrypted restored.
      setArmedAt(null);
      pushToast({ title: "Plaintext Bridge", body: "Reverted — encrypted send restored", tone: "success" });
    }
    prevArmed.current = bridgeArmed;
  }, [bridgeArmed]);
  useEffect(() => {
    if (!bridgeArmed || armedAt == null) return;
    const id = window.setInterval(() => {
      const left = Math.max(0, BRIDGE_WINDOW_MS - (Date.now() - armedAt));
      setRemainingMs(left);
    }, 250);
    return () => window.clearInterval(id);
  }, [bridgeArmed, armedAt]);

  // §5.3 — WINDOW BLUR DISARMS. A focus-loss is one of the sanctioned auto-reverts: the
  // operator looked away, so the one-shot clear window must close.
  useEffect(() => {
    const onBlur = (): void => api.disarmBridge();
    window.addEventListener("blur", onBlur);
    return () => window.removeEventListener("blur", onBlur);
  }, [api]);

  // --- READ-STATE: mark the OPEN room read whenever its traffic changes (item 2). The unread
  // badges (per-channel rows + the global rail total) fold off the capability's read-state, so
  // viewing a room clears its badge; OTHER rooms keep accruing unread until visited. ---
  useEffect(() => {
    api.markRoomRead(room);
  }, [api, room, messages.length]);

  // --- JUMP-TO-LATEST (item 3): keep the list pinned to newest ONLY when the operator is
  // already at the bottom; if they scrolled up to read history we do NOT yank them, and we
  // surface a scroll-to-bottom affordance instead. ---
  const [atBottom, setAtBottom] = useState(true);
  const onListScroll = (): void => {
    const el = listRef.current;
    if (!el) return;
    const dist = el.scrollHeight - el.scrollTop - el.clientHeight;
    setAtBottom(dist < 48);
  };
  useLayoutEffect(() => {
    const el = listRef.current;
    if (el && atBottom) el.scrollTop = el.scrollHeight;
    // atBottom intentionally omitted from deps: we only re-pin on new traffic, reading the
    // latest atBottom via the ref-driven scroll handler.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages.length]);
  const jumpToLatest = (): void => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
    setAtBottom(true);
  };

  const submit = (): void => {
    const text = draft.trim();
    if (!text) return;
    setSendError(null);
    // Sealed by default; if the bridge is armed for THIS room the adapter routes this one
    // send to the clear path + auto-reverts (one-shot). The room-marking gate (the class
    // CEILING + the releasability clamp) still runs either way.
    void api
      .send(text, room, effectiveMarking, rel)
      .then(() => {
        setDraft("");
        jumpToLatest();
      })
      .catch((e: unknown) => {
        // Fail-closed: a refused send (ROOM_CEILING / seal failure) KEEPS the draft + surfaces
        // the error (never silently drops, never sends clear on error — §2.1 fail-closed
        // asymmetry). The ROOM_CEILING text points the operator at opening a higher room.
        setSendError(e instanceof Error ? e.message : "send failed");
      });
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  const switchRoom = (next: string): void => {
    if (next === room) return;
    void api.openRoom(next).catch(() => {/* a JOIN that suppresses presence is benign */});
  };

  const openDm = (otherId: string): void => {
    setDmOpen(false);
    void api.openDm(otherId).catch((e: unknown) =>
      pushToast({ title: "Direct message", body: e instanceof Error ? e.message : "failed", tone: "alert" }),
    );
  };

  // --- the per-message confirm → arm flow (§5.3) ---
  const confirmAndArm = (): void => {
    setConfirmBridge(false);
    const ok = api.armBridge();
    if (!ok) {
      // armBridge re-checks legacy presence + refuses if it vanished between open + confirm.
      pushToast({ title: "Plaintext Bridge", body: "No legacy client present — bridge refused", tone: "alert" });
    }
  };

  // Mention resolution: a handle is "known" if it is present in the current room or is you.
  const presentSet = useMemo(() => {
    const set = new Set<string>([author.toLowerCase()]);
    for (const p of currentRowOf(rooms, room)?.present ?? []) set.add(p.toLowerCase());
    return set;
  }, [rooms, room, author]);
  const isKnownHandle = (h: string): boolean => presentSet.has(h.toLowerCase());

  return (
    <section
      // SINGLE-CHROME (2026-07-06): the HOST owns the outer frame — the LeftSlideout `chat`
      // pane (its `.slideout` card + `.slideout__head` "CHAT / ENCRYPTED · E2E / ✕" title bar)
      // OR, for a third-party add-in mount, the <PanelHost> dock (its DOCK_BASE border/shadow).
      // So this panel renders FRAMELESS content that FILLS the host body — matching the pure
      // slideout siblings (search/request/audit render bare `.slideout-*` divs, no `.panel`).
      // We deliberately do NOT carry the shared `.panel` housing here: `.panel` would draw a
      // SECOND border/radius/shadow + a duplicate teal spine tick INSIDE the host frame — the
      // "two nested windows" double-chrome bug. The only self-framed state is `.chat--expanded`
      // (a deliberate large floating surface over the map), which re-adds its own frame in CSS.
      className={`chat${bridgeArmed ? " chat--armed" : ""}${expanded ? " chat--expanded" : ""}`}
      aria-label="Encrypted mission-room chat"
    >
      {/* ── HEADER: room identity + room-class chip + collapse/expand controls ── */}
      <header className="chat__header">
        <div className="chat__header-row">
          <button
            type="button"
            className={`chat__rooms-toggle${roomsCollapsed ? "" : " chat__rooms-toggle--open"}`}
            aria-label={roomsCollapsed ? "Show conversations" : "Hide conversations"}
            aria-pressed={!roomsCollapsed}
            title={roomsCollapsed ? "Show conversations" : "Hide conversations"}
            onClick={() => setRoomsCollapsed((v) => !v)}
          >
            ☰
          </button>
          <div className="chat__roomtab">
            <span className="chat__eyebrow">{isDmRoom(room) ? "Direct Message" : "Mission Room"}</span>
            <span className="chat__room">{currentRoomLabel(rooms, room, author, callsignOf)}</span>
          </div>
          <ClassChip classification={roomClass} title={`Room class — ${classLabel(roomClass)} (seal for all room traffic; ceiling for message content)`} />
          <button
            type="button"
            className="chat__expand-btn"
            aria-label={expanded ? "Collapse chat" : "Expand chat"}
            aria-pressed={expanded}
            title={expanded ? "Collapse chat to the dock" : "Expand chat to a large view"}
            onClick={() => setExpanded((v) => !v)}
          >
            {expanded ? "⤡" : "⤢"}
          </button>
        </div>
        <div className="chat__sub">
          {messages.length} message{messages.length === 1 ? "" : "s"} · you are {author}
          {legacy.length > 0 ? (
            <span className="chat__legacy-note" title={`Legacy clients present: ${legacy.join(", ")}`}>
              {" · "}{legacy.length} legacy present
            </span>
          ) : null}
        </div>
      </header>

      {/* ── ARMED HAZARD BANNER (§5.3): full-width amber/black, header edge ── */}
      {bridgeArmed ? (
        <div className="chat__hazard" role="alert">
          <span className="chat__hazard-label">⚠ PLAINTEXT BRIDGE ARMED</span>
          <span className="chat__hazard-detail">
            next message sends UNENCRYPTED to {legacy.length ? legacy.join(", ") : "legacy clients"}
          </span>
          <span className="chat__hazard-count">{Math.ceil(remainingMs / 1000)}s</span>
          <button type="button" className="chat__hazard-cancel" onClick={() => api.disarmBridge()}>
            CANCEL
          </button>
        </div>
      ) : null}

      {/* ── HONEST SESSION BANNER: an expired token 401s at the KAS and would otherwise
          masquerade as clearance denials ("body sealed" on your own traffic) + a dead feed.
          Truth + the recovery path, never a silent degradation. ── */}
      {auth === "expired" ? (
        <div className="chat__session" role="alert">
          <span className="chat__session-label">🔑 SESSION EXPIRED</span>
          <span className="chat__session-detail">
            {api.canRenew()
              ? "renewing automatically — sealed rows recover on the next refresh"
              : "sign in again (persona menu) — federated sessions don't auto-renew"}
          </span>
          <button
            type="button"
            className="chat__session-retry"
            title="Ask the KAS again for every sealed row (safe — a genuine clearance denial stays denied)"
            onClick={() => void api.retryRedacted()}
          >
            RETRY UNLOCK
          </button>
        </div>
      ) : null}

      {/* ── BODY: an always-visible ROOM LIST (item 5) + the message/composer column. In the
          expanded (large) mode they sit side-by-side; in the dock they stack with the list
          collapsible for more message room (item 7). ── */}
      <div className="chat__body">
        {!roomsCollapsed ? (
          <RoomList
            rooms={rooms}
            current={room}
            self={author}
            callsignOf={callsignOf}
            onPick={switchRoom}
            onLeave={(r) => api.leaveRoom(r)}
            onNewDm={() => setDmOpen(true)}
            onNewChannel={() => setCreateOpen(true)}
          />
        ) : null}

        <div className="chat__main">
          {/* ── CREATE ROOM (§7.9 L2): creator-set classification + releasability → the room
              marking (the seal for all room traffic + the content-pick ceiling) ── */}
          {createOpen ? (
            <CreateRoom
              onCancel={() => setCreateOpen(false)}
              onCreate={(name, cls, releasability) => {
                setCreateOpen(false);
                void api.openRoom(name, cls, releasability).catch((e: unknown) =>
                  pushToast({ title: "Create room", body: e instanceof Error ? e.message : "failed", tone: "alert" }),
                );
              }}
            />
          ) : null}

          {/* ── NEW DM (item 6): a person picker that starts a direct message ── */}
          {dmOpen ? (
            <DmPicker contacts={api.contacts()} self={author} onCancel={() => setDmOpen(false)} onPick={openDm} />
          ) : null}

          {/* ── MESSAGE LIST (§7.9): cleared body (with @mention highlight) + banner, OR the
              marked placeholder. Jump-to-latest rides the bottom-right when scrolled up. ── */}
          <div className="chat__list-wrap">
            <div className="chat__list" ref={listRef} role="log" aria-live="polite" onScroll={onListScroll}>
              {messages.length === 0 ? (
                <div className="chat__empty">No traffic in this room yet</div>
              ) : (
                messages.map((m) => (
                  <MessageRow
                    key={m.id}
                    msg={m}
                    mine={m.author === author}
                    roomClass={roomClass}
                    self={author}
                    isKnownHandle={isKnownHandle}
                    displayName={m.senderCallsign ?? callsignOf.get(m.author) ?? m.author}
                    onRetry={() => void api.retryRedacted()}
                  />
                ))
              )}
            </div>
            {!atBottom && messages.length > 0 ? (
              <button type="button" className="chat__jump" onClick={jumpToLatest} aria-label="Jump to latest messages">
                ↓ Latest
              </button>
            ) : null}
          </div>

          {/* ── ROSTER (§7.5): presence + the §5.2 capability shield (collapsible, item 7) ── */}
          <Roster
            rooms={rooms}
            room={room}
            legacy={legacy}
            collapsed={rosterCollapsed}
            onToggle={() => setRosterCollapsed((v) => !v)}
          />

          {/* ── COMPOSER (§7.2) + the [TDF] marking selector (§7.9) + the bridge control (§5.3) ── */}
          <div className="chat__composer">
            <div className="chat__marking">
              <label className="chat__marking-field">
                <span className="chat__marking-tag">[TDF]</span>
                <select
                  className="chat__select"
                  aria-label="Message classification"
                  value={effectiveMarking}
                  onChange={(e) => setMarking(e.target.value)}
                >
                  {/* CEILING (L2 flip): only picks AT-OR-BELOW the room class are selectable;
                      an above-room pick is greyed — that traffic belongs in a higher room. */}
                  {CLASSIFICATIONS.map((c) => (
                    <option key={c} value={c} disabled={!withinCeiling(c, roomClass)}>
                      {classLabel(c)}
                      {!withinCeiling(c, roomClass) ? " (above room)" : ""}
                    </option>
                  ))}
                </select>
              </label>
              <RelPicker value={rel} onChange={setRel} />
              {/* The bridge control is HIDDEN unless a legacy client is genuinely present (§5.3). */}
              {bridgeReachable && !bridgeArmed ? (
                <button
                  type="button"
                  className="chat__bridge-btn"
                  title={`Send the NEXT message in the CLEAR to ${legacy.join(", ")}`}
                  onClick={() => setConfirmBridge(true)}
                >
                  ⚠ Plaintext Bridge
                </button>
              ) : null}
            </div>
            <div className="chat__composer-row">
              <input
                className={`chat__input${bridgeArmed ? " chat__input--armed" : ""}`}
                type="text"
                value={draft}
                placeholder={bridgeArmed ? "Plaintext bridge ARMED — next send is CLEAR…" : `Message ${composerHint(room, author, callsignOf)}…`}
                aria-label={`Message room ${room}`}
                onChange={(e) => {
                  setDraft(e.target.value);
                  if (sendError) setSendError(null);
                }}
                onKeyDown={onKeyDown}
              />
              <button className="chat__send" type="button" onClick={submit} disabled={draft.trim().length === 0}>
                {bridgeArmed ? "Send ⚠" : "Send"}
              </button>
            </div>
            {sendError ? <div className="chat__send-error" role="alert">{sendError}</div> : null}
          </div>
        </div>
      </div>

      {/* ── BRIDGE CONFIRM MODAL (§5.3): MUST display the actual legacy callsigns ── */}
      {confirmBridge ? (
        <BridgeConfirm
          callsigns={legacy}
          onCancel={() => setConfirmBridge(false)}
          onConfirm={confirmAndArm}
        />
      ) : null}
    </section>
  );
}

/** The current row helper (kept local so the mention resolver + label share one lookup). */
function currentRowOf(rooms: RoomRow[], room: string): RoomRow | undefined {
  return rooms.find((r) => r.room === room);
}

/** The current room's display label (DM peer callsign-when-known for a DM, descriptor name, else the key). */
function currentRoomLabel(rooms: RoomRow[], room: string, self: string, callsignOf?: Map<string, string>): string {
  if (isDmRoom(room)) {
    const peer = dmPeer(room, self);
    return peer ? peerDisplay(peer, callsignOf) : room;
  }
  const row = currentRowOf(rooms, room);
  return row?.displayName ?? room;
}

/** The composer placeholder hint: "@peer" (callsign when known) for a DM, else "#room". */
function composerHint(room: string, self: string, callsignOf?: Map<string, string>): string {
  if (isDmRoom(room)) {
    const peer = dmPeer(room, self);
    return `@${peer ? peerDisplay(peer, callsignOf) : room}`;
  }
  return `#${room}`;
}

/** FAIL-CLOSED per-message verify (Guardrail 2): runs the client-side WebCrypto verify over the
 *  SEAL-BOUND input once per message. Defaults to "asserted" (never "signed") until — and unless
 *  — verification proves the signature binds THIS seal + id|room|sender. `checked` flips true when
 *  the async verify settles (so the badge shows a neutral "checking…" instead of a false
 *  "asserted" flash on a message that is about to verify as signed). */
function useMessageVerify(msg: ChatMessage): { state: SignatureVerifyState; checked: boolean } {
  const [state, setState] = useState<SignatureVerifyState>("asserted");
  const [checked, setChecked] = useState(false);
  useEffect(() => {
    let alive = true;
    setChecked(false);
    void verifyMessageSignature({
      signature: msg.signature,
      sealMaterial: msg.encryptedRef,
      id: msg.id,
      room: msg.room,
      sender: msg.author,
    }).then((s) => {
      if (alive) {
        setState(s);
        setChecked(true);
      }
    });
    return () => {
      alive = false;
    };
  }, [msg.signature, msg.encryptedRef, msg.id, msg.room, msg.author]);
  return { state, checked };
}

/** The compact per-row trust badge — always clickable → opens the trust card. FAIL-CLOSED:
 *  "signed" = client-verified (green); "invalid" = a signature is present but FAILS (red);
 *  "asserted" = no/unverified signature (muted — the honest current state). A present-but-not-
 *  yet-checked signature shows a neutral "checking…" (never a premature "signed"). */
function TrustBadge({
  state,
  checked,
  hasSignature,
  onClick,
}: {
  state: SignatureVerifyState;
  checked: boolean;
  hasSignature: boolean;
  onClick: () => void;
}): JSX.Element {
  const pending = hasSignature && !checked;
  const meta = pending
    ? { cls: "checking", glyph: "🔏", label: "checking…", title: "Verifying the client signature over the seal…" }
    : state === "signed"
      ? { cls: "signed", glyph: "✅", label: "signed", title: "Signed (demo-tier, client-verified) — click for details" }
      : state === "invalid"
        ? { cls: "invalid", glyph: "❌", label: "invalid", title: "Signature present but FAILS verification — click for details" }
        : { cls: "asserted", glyph: "⚠", label: "asserted", title: "Asserted — not signed (click for the honest trust line + raw ZTDF manifest)" };
  return (
    <button
      type="button"
      className={`chat__trust-badge chat__trust-badge--${meta.cls}`}
      title={meta.title}
      aria-haspopup="dialog"
      onClick={onClick}
    >
      <span aria-hidden="true">{meta.glyph}</span> {meta.label}
    </button>
  );
}

/** §7.9 message render. A cleared body shows the TEXT (with @mention highlighting) + a
 *  per-message classification banner; a redacted message shows the MARKED PLACEHOLDER and
 *  NEVER the body. Fail-closed: a message whose marking is unparseable still renders the
 *  placeholder (never the body) since `redacted` is the capability's authoritative shell-only
 *  signal — @mention parsing runs ONLY on an already-decoded (granted) body. */
function MessageRow({
  msg,
  mine,
  roomClass,
  self,
  isKnownHandle,
  displayName,
  onRetry,
}: {
  msg: ChatMessage;
  mine: boolean;
  roomClass: string;
  self: string;
  isKnownHandle: (handle: string) => boolean;
  /** Callsign-first display name (senderCallsign → presence callsign → raw author id). */
  displayName: string;
  /** Re-ask the KAS for sealed rows (session recovery; a genuine denial stays denied). */
  onRetry: () => void;
}): JSX.Element {
  // Click a message → the trust card (signer + FAIL-CLOSED verify badge + the raw ZTDF manifest).
  const [trustOpen, setTrustOpen] = useState(false);
  // FAIL-CLOSED per-row verify badge (Guardrail 2): default "asserted" until the async WebCrypto
  // verify over the SEAL-BOUND input resolves — a row NEVER reads "signed" before it is proven.
  const { state: verifyState, checked: verifyChecked } = useMessageVerify(msg);
  // The per-message chip shows the operator's CONTENT marking when the sealed body carried
  // one (contentClassification, <= the envelope class by construction — L2 ceiling flip),
  // falling back to the envelope class (old messages / bridged clear sends / redacted
  // shells), then to the room class. The chip can show BELOW the room class (the content
  // level); the transport seal is always exactly the room class.
  const cls = msg.contentClassification ?? msg.classification ?? roomClass;
  const sealed = !!msg.encryptedRef;
  return (
    <div className={`chat__msg${mine ? " chat__msg--mine" : ""}`}>
      <div className="chat__msg-meta">
        <button
          type="button"
          className="chat__author chat__author--btn"
          title={`${msg.author} — click for trust details (signature + raw ZTDF manifest)`}
          aria-haspopup="dialog"
          onClick={() => setTrustOpen(true)}
        >
          {displayName}
        </button>
        <span className="chat__time">{clock(msg.time)}</span>
        <ClassChip classification={cls} />
        {sealed ? (
          <span className="chat__lock" title="Body sealed as a ZTDF — released per-reader by the KAS">🔏</span>
        ) : null}
        {msg.redacted ? <span className="chat__lock" title="Body sealed — not released to you">🔒</span> : null}
        {/* FAIL-CLOSED trust badge — click to open the full trust card. */}
        <TrustBadge
          state={verifyState}
          checked={verifyChecked}
          hasSignature={!!msg.signature}
          onClick={() => setTrustOpen(true)}
        />
      </div>
      {trustOpen ? (
        <MessageTrustCard msg={msg} displayName={displayName} onClose={() => setTrustOpen(false)} />
      ) : null}
      {msg.redacted ? (
        // NEVER the body — the marked placeholder only (§7.9 / §8.4) + the session-recovery
        // retry (safe: the KAS re-decides; a genuine clearance denial stays denied).
        <div className="chat__text chat__text--sealed">
          🔒 PROTECTED {classLabel(cls)} — body sealed
          <button
            type="button"
            className="chat__retry"
            title="Ask the KAS again (recovers rows sealed by an expired session; a genuine denial stays denied)"
            onClick={onRetry}
          >
            retry
          </button>
        </div>
      ) : (
        <div className="chat__text">{renderMessageBody(msg.body ?? "", { self, isKnownHandle })}</div>
      )}
    </div>
  );
}

/** §7.1 ALWAYS-VISIBLE room list (item 5): the Glomar-filtered api.rooms() fold, sectioned,
 *  newest-on-top within each section, each row carrying its UNREAD badge (item 2). A DENIED
 *  room is simply absent from `rooms` — there is NO "denied" row here (absence IS the Glomar
 *  behavior). The header carries the first-class "+ New DM" (item 6) + "+ New Channel" entry
 *  points. */
function RoomList({
  rooms,
  current,
  self,
  callsignOf,
  onPick,
  onLeave,
  onNewDm,
  onNewChannel,
}: {
  rooms: RoomRow[];
  current: string;
  self: string;
  callsignOf: Map<string, string>;
  onPick: (room: string) => void;
  onLeave: (room: string) => void;
  onNewDm: () => void;
  onNewChannel: () => void;
}): JSX.Element {
  const sections: Section[] = ["broadcast", "teams", "groups", "contacts"];
  const bySection = new Map<Section, RoomRow[]>();
  for (const r of rooms) {
    const s = sectionOf(r);
    const list = bySection.get(s) ?? [];
    list.push(r);
    bySection.set(s, list);
  }
  // Newest-on-top within each section (rooms() already sorts; keep its order).
  return (
    <div className="chat__roomlist" role="navigation" aria-label="Conversations">
      <div className="chat__roomlist-head">
        <span className="chat__roomlist-title">Conversations</span>
        <div className="chat__roomlist-actions">
          <button type="button" className="chat__new chat__new--dm" onClick={onNewDm} title="Message someone directly">
            + New DM
          </button>
          <button type="button" className="chat__new" onClick={onNewChannel} title="Create a channel">
            + Channel
          </button>
        </div>
      </div>
      <div className="chat__roomlist-scroll">
        {sections.map((s) => {
          const list = bySection.get(s);
          if (!list || list.length === 0) return null;
          return (
            <div key={s} className="chat__roomlist-section">
              <div className="chat__roomlist-label">{SECTION_LABEL[s]}</div>
              {list.map((r) => {
                // C3-CHAT soft-leave — a MANDATORY room (fixed broadcast + classification-floor)
                // shows NO leave affordance; only OPTIONAL rooms are removable (soft-leave/rejoin).
                // The leave (×) is a SIBLING of the open button (no nested interactive) and stops
                // the click from also switching rooms. Glomar-safe: hide only ever REMOVES a row.
                const removable = !r.mandatory;
                return (
                  <div
                    key={r.room}
                    role="option"
                    aria-selected={r.room === current}
                    className={`chat__roomrow${r.room === current ? " chat__roomrow--active" : ""}${
                      r.unread > 0 ? " chat__roomrow--unread" : ""
                    }`}
                  >
                    <button
                      type="button"
                      className="chat__roomrow-open"
                      aria-label={`Open ${roomLabel(r, self, callsignOf)}${r.unread > 0 ? `, ${r.unread} unread` : ""}`}
                      onClick={() => onPick(r.room)}
                    >
                      <span className="chat__roomrow-main">
                        <span className="chat__roomrow-name">
                          {isDmRoom(r.room) ? <span className="chat__dm-glyph" aria-hidden="true">@</span> : null}
                          {roomLabel(r, self, callsignOf)}
                        </span>
                        <span className="chat__roomrow-preview">
                          {r.lastMessagePreview ?? (r.messageCount === 0 ? "no messages yet" : "🔒 sealed")}
                        </span>
                      </span>
                      <span className="chat__roomrow-side">
                        {/* UNREAD badge (item 2): renders ONLY when unread>0, off the SAME post-seam fold. */}
                        {renderUnreadBadge(r.unread, { className: "chat__unread chat__roomrow-unread", label: `${r.unread} unread in ${roomLabel(r, self, callsignOf)}` })}
                        <ClassChip classification={r.classification} />
                        {r.lastMessageTime ? <span className="chat__roomrow-time">{clock(r.lastMessageTime)}</span> : null}
                      </span>
                    </button>
                    {removable ? (
                      <button
                        type="button"
                        className="chat__roomrow-leave"
                        title={`Leave ${roomLabel(r, self, callsignOf)}`}
                        aria-label={`Leave ${roomLabel(r, self, callsignOf)}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          onLeave(r.room);
                        }}
                      >
                        ×
                      </button>
                    ) : null}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** Item 6 — the "+ New DM" PEOPLE PICKER. A DM is a room with one other person; this resolves
 *  candidates from presence (api.people(), a Glomar-safe fold over rooms you can already see)
 *  and also accepts a free-typed callsign so you can DM someone you know by name. Picking a
 *  person opens (creating if needed) the canonical 1:1 DM room via api.openDm. */
function DmPicker({
  contacts,
  self,
  onCancel,
  onPick,
}: {
  /** Known people: canonical id + display callsign when known (client-asserted). */
  contacts: { id: string; callsign?: string }[];
  self: string;
  onCancel: () => void;
  onPick: (otherId: string) => void;
}): JSX.Element {
  const [query, setQuery] = useState("");
  const q = query.trim();
  const ql = q.toLowerCase();
  // CALLSIGN-AWARE autocomplete: match on the callsign OR the id; picking always passes the
  // CANONICAL id, so both peers' DM room keys converge (dm:<a>~<b> over ids, never over a
  // typed display name — the earlier divergence bug class).
  const filtered = contacts.filter(
    (c) => c.id.toLowerCase().includes(ql) || (c.callsign ?? "").toLowerCase().includes(ql),
  );
  // A typed name that resolves to EXACTLY ONE known contact (by callsign or id) picks that
  // contact's canonical id on Enter. A wholly-unknown name may fall back to the raw typed
  // string ONLY when it looks like a CANONICAL ID (a federated email, or a lowercase persona
  // id) — never a callsign-shaped display name. Reason: dmRoomKey keys on canonical ids, so
  // creating a DM from a raw callsign (e.g. "DOOGIE-42") that isn't yet in contacts() mints a
  // room keyed on the DISPLAY NAME — permanently divergent from the peer's id-keyed room (the
  // "dapple sees 2, gmail sees 4" bug). A callsign you haven't seen can't be canonicalized;
  // wait for the peer to appear (contacts() learns callsigns from messages) or address by id.
  const exactMatches = contacts.filter(
    (c) => c.id.toLowerCase() === ql || (c.callsign ?? "").toLowerCase() === ql,
  );
  const looksLikeCanonicalId = q.includes("@") || /^[a-z0-9._-]+$/.test(q);
  const exactTyped =
    q.length > 0 && exactMatches.length === 0 && ql !== self.toLowerCase() && looksLikeCanonicalId;
  // A callsign-shaped string with no contact match: can't mint a room safely — hint instead.
  const unresolvableCallsign =
    q.length > 0 && exactMatches.length === 0 && filtered.length === 0 && !looksLikeCanonicalId;
  return (
    <div className="chat__dm" role="dialog" aria-label="New direct message">
      <div className="chat__dm-head">Message someone</div>
      <input
        className="chat__input"
        type="text"
        autoFocus
        placeholder="Search callsigns or ids…"
        aria-label="Person to message"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={(e) => {
          if (e.key !== "Enter") return;
          if (exactMatches.length === 1) onPick(exactMatches[0].id);
          else if (filtered.length === 1) onPick(filtered[0].id);
          else if (exactTyped) onPick(q);
        }}
      />
      <div className="chat__dm-people">
        {unresolvableCallsign ? (
          <div className="chat__dm-empty">“{q}” hasn’t appeared yet — wait for them to speak, or address them by id (email / username).</div>
        ) : filtered.length === 0 && !exactTyped ? (
          <div className="chat__dm-empty">No one to message yet — pick a contact once they’re online.</div>
        ) : (
          <>
            {filtered.map((c) => (
              <button key={c.id} type="button" className="chat__dm-person" onClick={() => onPick(c.id)}>
                <span className="chat__dm-avatar" aria-hidden="true">{initials(c.callsign ?? c.id)}</span>
                <span className="chat__dm-person-name">{c.callsign ?? c.id}</span>
                {c.callsign ? <span className="chat__dm-person-id">{c.id}</span> : null}
              </button>
            ))}
            {exactTyped ? (
              <button type="button" className="chat__dm-person chat__dm-person--new" onClick={() => onPick(q)}>
                <span className="chat__dm-avatar" aria-hidden="true">@</span>
                <span className="chat__dm-person-name">Message “{q}”</span>
              </button>
            ) : null}
          </>
        )}
      </div>
      <div className="chat__dm-actions">
        <button type="button" className="chat__btn-ghost" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

/** Two-letter initials for a DM avatar (FI-LI style; falls back to the first 2 chars). */
function initials(id: string): string {
  const parts = id.split(/[\s._-]+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return id.slice(0, 2).toUpperCase();
}

/** §7.9 L2 — the CREATOR-SET room classification dropdown. The creator picks the room's
 *  classification + releasability; that becomes the ROOM MARKING (calls
 *  openRoom(name, cls, rel), which publishes the descriptor at that marking): the seal for
 *  all room traffic and the CEILING for message content picks. */
function CreateRoom({
  onCancel,
  onCreate,
}: {
  onCancel: () => void;
  onCreate: (name: string, classification: string, releasability: string[]) => void;
}): JSX.Element {
  const [name, setName] = useState("");
  const [cls, setCls] = useState<string>("UNCLASSIFIED");
  // Default room releasability = `[]` (public / unrestricted within the classification).
  // The creator may chip-in a coalition to narrow it; `[]` validates + seals (a coalition tag
  // like NATO is NOT a legal defense.local releasability and threw on seal).
  const [rel, setRel] = useState<string[]>([]);
  const valid = name.trim().length > 0;
  return (
    <div className="chat__create" role="dialog" aria-label="New channel">
      <div className="chat__create-head">New channel</div>
      <input
        className="chat__input"
        type="text"
        autoFocus
        placeholder="Channel name…"
        aria-label="Channel name"
        value={name}
        onChange={(e) => setName(e.target.value)}
      />
      <label className="chat__marking-field">
        <span className="chat__marking-tag">Class</span>
        <select className="chat__select" aria-label="Room classification" value={cls} onChange={(e) => setCls(e.target.value)}>
          {CLASSIFICATIONS.map((c) => (
            <option key={c} value={c}>{classLabel(c)}</option>
          ))}
        </select>
      </label>
      <RelPicker value={rel} onChange={setRel} />
      <div className="chat__create-actions">
        <button type="button" className="chat__btn-ghost" onClick={onCancel}>Cancel</button>
        <button type="button" className="chat__btn-primary" disabled={!valid} onClick={() => onCreate(name.trim(), cls, rel)}>
          Create
        </button>
      </div>
    </div>
  );
}

/** A compact multi-select for releasability (defense.local vocab). */
function RelPicker({ value, onChange }: { value: string[]; onChange: (v: string[]) => void }): JSX.Element {
  const toggle = (code: string): void => {
    onChange(value.includes(code) ? value.filter((c) => c !== code) : [...value, code]);
  };
  return (
    <div className="chat__rel" role="group" aria-label="Releasability">
      {RELEASABILITIES.map((code) => (
        <button
          key={code}
          type="button"
          className={`chat__rel-chip${value.includes(code) ? " chat__rel-chip--on" : ""}`}
          aria-pressed={value.includes(code)}
          onClick={() => toggle(code)}
        >
          {code}
        </button>
      ))}
    </div>
  );
}

/** §7.5 roster: contacts present in the current room + the §5.2 capability SHIELD glyph.
 *  Folds off rooms() present[] + the legacy callsign set (the only capability signal the
 *  ChatApi surfaces): a legacy callsign → SLASHED hollow shield; a present non-legacy author
 *  → FILLED shield (capable); the acting author is "you". Collapsible (item 7) so the
 *  message list can take the full height when the operator wants it. */
function Roster({
  rooms,
  room,
  legacy,
  collapsed,
  onToggle,
}: {
  rooms: RoomRow[];
  room: string;
  legacy: string[];
  collapsed: boolean;
  onToggle: () => void;
}): JSX.Element {
  const row = rooms.find((r) => r.room === room);
  const present = row?.present ?? [];
  const legacySet = new Set(legacy);
  // Union of present authors + any legacy callsigns (a legacy client may be present-but-stale
  // for messages yet still in the legacy set — show it so the operator sees the bridge target).
  const contacts = [...new Set([...present, ...legacy])];
  const count = contacts.length;
  return (
    <div className={`chat__roster-wrap${collapsed ? " chat__roster-wrap--collapsed" : ""}`}>
      <button
        type="button"
        className="chat__roster-toggle"
        aria-expanded={!collapsed}
        title={collapsed ? "Show roster" : "Hide roster"}
        onClick={onToggle}
      >
        <span className="chat__roster-toggle-caret" aria-hidden="true">{collapsed ? "▸" : "▾"}</span>
        Roster · {count}
      </button>
      {!collapsed ? (
        count === 0 ? (
          <div className="chat__roster chat__roster--empty">No one present</div>
        ) : (
          <div className="chat__roster" aria-label="Roster">
            {contacts.map((uid) => {
              const isLegacy = legacySet.has(uid);
              return (
                <span key={uid} className={`chat__contact${isLegacy ? " chat__contact--legacy" : ""}`}>
                  <ShieldGlyph verdict={isLegacy ? "legacy" : "capable"} />
                  <span className="chat__contact-name">{uid}</span>
                </span>
              );
            })}
          </div>
        )
      ) : null}
    </div>
  );
}

/** §5.2 capability shield glyph. capable = filled shield (🛡), legacy = slashed/hollow
 *  (the muted shield with a slash, "cannot open sealed bodies"), unknown = dimmed ?. */
function ShieldGlyph({ verdict }: { verdict: "capable" | "legacy" | "unknown" }): JSX.Element {
  if (verdict === "capable") {
    return <span className="chat__shield chat__shield--capable" title="TDF-capable — can open sealed bodies" aria-label="capable">🛡</span>;
  }
  if (verdict === "legacy") {
    return <span className="chat__shield chat__shield--legacy" title="Legacy client — cannot open sealed bodies" aria-label="legacy">⛨</span>;
  }
  return <span className="chat__shield chat__shield--unknown" title="Unknown / stale capability" aria-label="unknown">?</span>;
}

/** §5.3 per-message confirm modal. It MUST display the ACTUAL legacy callsigns so the operator
 *  can sanity-check who they are bridging to (the mitigation for the logged insider-spoof). On
 *  confirm → armBridge(); cancel closes with no state change. */
function BridgeConfirm({
  callsigns,
  onCancel,
  onConfirm,
}: {
  callsigns: string[];
  onCancel: () => void;
  onConfirm: () => void;
}): JSX.Element {
  return (
    <div className="chat__modal-scrim" role="presentation" onClick={onCancel}>
      <div className="chat__modal" role="dialog" aria-modal="true" aria-label="Confirm plaintext bridge" onClick={(e) => e.stopPropagation()}>
        <div className="chat__modal-title">⚠ Send the NEXT message UNENCRYPTED?</div>
        <p className="chat__modal-body">
          This message body will be <strong>plaintext on the wire</strong> so these legacy clients can read it:
        </p>
        <ul className="chat__modal-callsigns">
          {callsigns.length === 0 ? (
            <li className="chat__modal-callsign chat__modal-callsign--none">no legacy clients present</li>
          ) : (
            callsigns.map((c) => <li key={c} className="chat__modal-callsign">{c}</li>)
          )}
        </ul>
        <p className="chat__modal-note">One-shot: auto-reverts after this send, a timeout, room switch, or focus loss.</p>
        <div className="chat__modal-actions">
          <button type="button" className="chat__btn-ghost" onClick={onCancel}>Cancel</button>
          <button
            type="button"
            className="chat__btn-hazard"
            disabled={callsigns.length === 0}
            onClick={onConfirm}
          >
            Arm plaintext bridge
          </button>
        </div>
      </div>
    </div>
  );
}
